import { Link, useNavigate, useParams } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { imgSrc } from "../../constants";
import { toast } from "react-toastify";
import Modal from "@/components/Modal";
import { LoadingSkeleton0, LoadingSkeleton1, LoadingSkeleton5 } from "../../components/Skeletons";
import Cart from "../../components/Cart";
import useCart from "../../hooks/useCart";
import {
  CameraIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InformationCircleIcon,
  LinkIcon,
  MapPinIcon,
  MinusCircleIcon,
  PhoneIcon,
  PlusCircleIcon,
} from "@heroicons/react/24/outline";

const Index = () => {
  const navigate = useNavigate();
  const { businessName } = useParams();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `${
            process.env.REACT_APP_API_URL
          }/OnlineOrdering/GetGroupedProducts?BusinessName=Foodb_${businessName.toLocaleLowerCase()}`
        );
        if (!response.ok) throw new Error();
        const jsonData = await response.json();
        if (jsonData.data.length <= 0) throw new Error();

        const categorizedProducts = jsonData.data[0].onlineProduct.reduce((acc, product) => {
          let category = acc.find((c) => c.id === product.subCategoryId);
          if (!category) {
            category = {
              id: product.subCategoryId,
              categoryName: product.subCategory,
              onlineProduct: [],
            };
            acc.push(category);
          }
          category.onlineProduct.push(product);
          return acc;
        }, []);

        setCategories(categorizedProducts.reverse());
      } catch (error) {
        toast.error("no such business");
        navigate("/");
      }
    };
    fetchData();
  }, []);

  return (
    <main className="relative w-full">
      <BusinessDetails businessName={businessName} />
      <CategoriesList data={categories} />
      <div className="relative z-0 py-6 md:py-12 flex flex-col-reverse md:flex-row justify-center items-start padding-x">
        <ProductsList data={categories} />
        <CartSection businessName={businessName} />
      </div>
    </main>
  );
};

const BusinessDetails = ({ businessName }) => {
  const [data, setData] = useState();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `${
            process.env.REACT_APP_API_URL
          }/OnlineOrdering/BusinessDetailData?BusinessName=Foodb_${businessName.toLocaleLowerCase()}`
        );
        if (!response.ok) throw new Error("Failed to fetch data");
        const jsonData = await response.json();
        if (jsonData.data.length <= 0) throw new Error();
        setData(JSON.parse(jsonData.data[0].onlineOrderingInfo).business[0]);
      } catch (error) {
        toast.error("no such business");
      }
    };
    if (!data) fetchData();
  }, []);

  if (data)
    return (
      <div className="w-full padding-x py-10">
        <div className="min-h-[22rem] flex flex-col-reverse md:flex-row justify-between items-start md:items-center gap-10 p-4 bg-white rounded-lg">
          <div className="w-full md:w-1/2 text-gray-500 text-sm ">
            <div>
              <h1 className="pb-10 font-bold text-xl md:text-2xl lg:text-3xl">{data.name}</h1>
              <button className="relative group cursor-auto flex items-center text-primary pb-1">
                <span className="cursor-pointer">Open until {data?.timings?.opening}</span>
                <ChevronDownIcon className="cursor-pointer group-hover:rotate-90 group-focus-within:rotate-90 w-4 h-4 ml-1" />
                <div className="group-hover:flex group-focus-within:flex hidden absolute top-6 left-0 bg-white shadow rounded-lg p-4">
                  <div className="flex flex-col gap-3 text-secondary/50">
                    <span className="font-semibold whitespace-nowrap">
                      {data?.timings?.opening} - {data?.timings?.closing}
                    </span>
                    <ul className="flex flex-wrap items-start gap-1">
                      {data?.days?.map((item, i) => (
                        <li className="bg-primary/10 rounded px-2" key={i}>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </button>
              <p>
                Just in - £{data?.deliveryCharge} delivery - £{data?.minimumOrder} minimum
              </p>
            </div>
            <div className="pt-6 flex flex-col gap-1">
              <a className="flex items-center py-[2px]" href={`tel:${data?.phone}`}>
                <PhoneIcon className="w-4 h-4 mr-1 mb-auto" />
                <span className="flex-1 hover:line-clamp-3 line-clamp-1">{data?.phone}</span>
              </a>
              {data?.website ? (
                <a className="flex items-center py-[2px]" href="#">
                  <LinkIcon className="w-4 h-4 mr-1 mb-auto" />
                  <span className="flex-1 hover:line-clamp-3 line-clamp-1">{data?.website}</span>
                </a>
              ) : null}
              {data?.instagram?.name ? (
                <a className="flex items-center py-[2px]" href="#">
                  <CameraIcon className="w-4 h-4 mr-1 mb-auto" />
                  <span className="flex-1 hover:line-clamp-3 line-clamp-1">{data?.instagram?.name}</span>
                </a>
              ) : null}
              <a className="flex items-center py-[2px]" href="#">
                <MapPinIcon className="w-4 h-4 mr-1 mb-auto" />
                <span className="flex-1 hover:line-clamp-3 line-clamp-1">
                  {data?.address}, {data?.location}, {data?.state}
                </span>
              </a>
              <a className="flex items-center py-[2px]" href="#">
                <InformationCircleIcon className="w-4 h-4 mr-1 mb-auto" />
                <span className="flex-1 hover:line-clamp-5 line-clamp-2">{data?.description}</span>
              </a>
            </div>
          </div>
          <div className="w-full h-72 md:w-1/2 mx-auto mb-auto rounded overflow-hidden">
            <img
              className="h-full w-full object-cover"
              src={data?.banner && data.banner !== "default" ? data.banner : "/images/brands/bunchiz.png"}
              alt="bunchiz"
            />
          </div>
        </div>
      </div>
    );
  else return <LoadingSkeleton5 />;
};

const CategoriesList = ({ data }) => {
  const [selectedSection, setSelectedSection] = useState(null);
  const sectionOffsets = [];
  const sectionHeights = [];
  const pos = useRef(null);
  const navRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.pageYOffset;
      let visibleSection = null;

      for (let i = 0; i < data?.length; i++) {
        const section = data[i];
        const sectionElement = document.getElementById(String(section.id));

        if (sectionElement) {
          if (!sectionOffsets[i]) {
            sectionOffsets[i] = sectionElement.offsetTop;
          }
          if (!sectionHeights[i]) {
            sectionHeights[i] = sectionElement.offsetHeight;
          }
          const sectionTop = sectionOffsets[i];
          const sectionBottom = sectionTop + sectionHeights[i];

          if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
            visibleSection = String(section.id);
            break;
          }
        }
      }
      if (visibleSection !== null && visibleSection !== selectedSection) {
        setSelectedSection(visibleSection);
        scrollToPos(visibleSection);
      }
    };
    const scrollToPos = (id) => {
      const navElement = navRef.current;
      const posElement = pos.current;

      if (navElement && posElement) {
        const navRect = navElement.getBoundingClientRect();
        const posRect = posElement.getBoundingClientRect();
        const posWidth = posRect.width;
        const posOffsetLeft = posRect.left - navRect.left - 100;
        const posOffsetRight = posRect.right - navRect.right + 100;

        if (posOffsetLeft < 0) {
          navElement.scrollLeft += posOffsetLeft - posWidth;
        } else if (posOffsetRight > 0) {
          navElement.scrollLeft += posOffsetRight + posWidth;
        }
      }
    };

    if (data && data.length > 0 && !selectedSection) setSelectedSection(data[data.length - 1].id);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [selectedSection, data]);

  if (data)
    return (
      <nav className="bg-white sticky top-0 z-10 flex">
        <div ref={navRef} className="flex flex-row-reverse margin-x px-0 overflow-x-scroll py-5 scroll-smooth">
          {data?.length > 0 ? (
            data?.map((item, i) => (
              <a
                key={item.id}
                href={"#" + item.id}
                ref={selectedSection == item.id ? pos : null}
                className={`${
                  selectedSection == item.id ? "bg-primary text-white" : ""
                } px-2 py-1 text-sm rounded whitespace-nowrap`}
              >
                {item.categoryName}
              </a>
            ))
          ) : (
            <LoadingSkeleton0 />
          )}
        </div>
      </nav>
    );
};

const ProductsList = ({ data }) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const { onAddToCart } = useCart();
  const [qty, setQty] = useState(1);
  if (data?.length > 0)
    return (
      <section className="flex flex-col-reverse w-full md:w-2/3 md:mr-5">
        {data.map((category, index) => (
          <div key={index} id={category.id} className="pt-20">
            <h1 className="pb-6 font-semibold text-lg md:text-xl lg:text-2xl">{category.categoryName}</h1>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {category.onlineProduct?.map((item, index) => (
                <div
                  key={index}
                  onClick={() => {
                    setSelectedItem(item);
                    setQty(1);
                  }}
                  className="cursor-pointer flex p-3 rounded bg-white "
                  id={item.categoryId}
                >
                  <div className="w-3/4 pl-1 text-gray-500">
                    <h2 className="text-black">{item.productName}</h2>
                    <p className="py-3 text-sm">{"cheese slice , angus beef patty."}</p>
                    <p>£ {item.price}</p>
                  </div>
                  <img
                    className="w-1/4 min-w-[6rem] rounded h-fit ml-3 overflow-hidden"
                    src={imgSrc + "food.jpg"}
                    alt={item.name}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
        {selectedItem ? (
          <Modal top="35vh" modalIsOpen={true} setModalIsOpen={setSelectedItem}>
            <div className="flex flex-col rounded overflow-hidden bg-white w-full h-fit">
              <button
                className="flex justify-center items-center absolute right-0 bg-white text-primary m-3 p-1 rounded-full border shadow-md"
                onClick={() => setSelectedItem(null)}
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
              <div className="px-4 md:px-8 py-3 bg-shade-light">
                <h1 className="text-lg md:text-xl">{selectedItem.productName}</h1>
                {/* <p className="pt-1 text-gray-500 text-sm font-light">
                  Sunset Sauce, lettuce, mayo, cheese slice, fired onion
                </p> */}
                <br />
                <br />
                <br />
                {/* <p className="pt-6 pb-2 text-gray-500 text-sm font-light underline flex items-center">
                  Allergies
                  <ChevronRightIcon className="w-3 h-3" />
                </p> */}
              </div>
              <div className="flex-1"></div>
              <div className="flex justify-evenly items-center py-4 bg-shade-light">
                <div className="flex justify-center items-center">
                  <button
                    onClick={() => setQty(qty - 1)}
                    disabled={qty <= 1}
                    className="p-2 hover:text-primary disabled:text-shade-dark"
                  >
                    <MinusCircleIcon className="w-6 h-6" />
                  </button>
                  <span className="p-2 font-semibold text-xl">{qty}</span>
                  <button
                    onClick={() => setQty(qty + 1)}
                    disabled={qty >= 24}
                    className="p-2 hover:text-primary disabled:text-shade-dark"
                  >
                    <PlusCircleIcon className="w-6 h-6" />
                  </button>
                </div>
                <button
                  onClick={() => {
                    onAddToCart(selectedItem, qty);
                    toast.success(`${qty}  ${selectedItem.productName} added.`);
                    setSelectedItem(null);
                  }}
                  className="bg-primary hover:bg-secondary text-white rounded px-6 md:px-12 py-3 w-fit"
                >
                  Add for £ {(selectedItem.price * qty).toFixed(2)}
                </button>
              </div>
            </div>
          </Modal>
        ) : null}
      </section>
    );
  else return <LoadingSkeleton1 />;
};

const CartSection = ({ businessName }) => {
  return (
    <section className="h-[70vh] flex flex-col relative md:sticky md:top-20 z-0 w-full md:w-1/3 md:ml-5 mt-6 md:mt-0">
      <div className="flex flex-col justify-between h-full bg-white p-2 rounded-lg">
        <Cart />
      </div>
      <Link
        to={`/${businessName}/checkout`}
        className="bg-primary hover:bg-secondary text-white text-lg text-center w-full inline-block md:text-xl rounded p-4 mt-4"
      >
        Checkout
      </Link>
    </section>
  );
};

export default Index;
